import { Head, Link } from '@inertiajs/react';

import {
    Btn,
    DesignFooter,
    DesignHeader,
    I,
    LawyerCardV,
    SectionHeader,
    UtilityBar,
    type LawyerCardData,
} from '@/Components/Design';

type ServerCard = {
    id: number;
    slug: string;
    name: string;
    firm: string | null;
    city: string | null;
    state: string | null;
    stateAbbr: string | null;
    yearsLicensed: number | null;
    languages: string[];
    reviewsAvg: number | null;
    reviewsCount: number;
    practiceAreas: Array<{ slug: string; name: string }>;
    featured: boolean;
    headshotKey: string | null;
    specializes?: boolean;
};

type Props = {
    practiceArea: { slug: string; name: string; description: string; icon: string | null; specialistsCount?: number };
    lawyers: ServerCard[];
};

function initialsOf(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    return ((parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase() || 'AN';
}

function hueOf(slug: string): number {
    let h = 0;
    for (let i = 0; i < slug.length; i++) h = (h * 31 + slug.charCodeAt(i)) | 0;
    return Math.abs(h) % 6;
}

function toCard(l: ServerCard, i: number): LawyerCardData {
    return {
        name: l.name,
        firm: l.firm ?? '',
        city: l.city ?? '',
        state: l.stateAbbr ?? '',
        rating: l.reviewsAvg ?? 0,
        reviews: l.reviewsCount,
        years: l.yearsLicensed ?? 0,
        areas: l.practiceAreas.map((p) => p.name),
        langs: l.languages,
        initials: initialsOf(l.name),
        hue: hueOf(l.slug) ?? i,
        featured: l.featured,
        href: `/${l.slug}`,
        headshotUrl: l.headshotKey ? `/cdn/lawyers/320/${l.headshotKey}.webp` : null,
    };
}

export default function PracticeArea({ practiceArea, lawyers }: Props) {
    return (
        <>
            <Head>
                <title>{`${practiceArea.name} lawyers — Find a specialist`}</title>
            </Head>
            <div style={{ background: 'var(--paper)', minHeight: '100vh' }}>
                <UtilityBar />
                <DesignHeader active="Practice Areas" />

                {/* Hero */}
                <section data-pa-section="hero" style={{ maxWidth: 1440, margin: '0 auto', padding: '40px 56px 32px' }}>
                    <nav aria-label="Breadcrumb">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--ink-mute)', marginBottom: 18 }}>
                        <Link href="/" style={{ color: 'inherit', textDecoration: 'none' }}>Home</Link>
                        <span>/</span>
                        <span>Practice Areas</span>
                        <span>/</span>
                        <span aria-current="page" style={{ color: 'var(--ink)' }}>{practiceArea.name}</span>
                    </div></nav>
                    <div data-pa-hero-grid style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 48, alignItems: 'center' }}>
                        <div>
                            <div style={{ fontSize: 12, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ember)', marginBottom: 10, fontWeight: 600 }}>Practice area</div>
                            <h1 style={{ fontSize: 64, lineHeight: 1.02, letterSpacing: '-0.025em', marginBottom: 18, fontFamily: 'var(--font-serif)', fontWeight: 500 }}>
                                {practiceArea.name}<br />lawyers near you.
                            </h1>
                            <p style={{ fontSize: 18, color: 'var(--ink-soft)', lineHeight: 1.55, maxWidth: 560, marginBottom: 24 }}>
                                {practiceArea.description}
                            </p>
                            <div data-pa-cta-row style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                <Btn kind="primary" size="lg" icon={I.search} href={`/search?practice=${practiceArea.slug}`}>Find {practiceArea.name.toLowerCase()} lawyers</Btn>
                                <Btn kind="secondary" size="lg" icon={I.phone} href="/get-matched">Get matched</Btn>
                            </div>
                        </div>
                        {/* Right-side "at a glance" card removed — every value was filler (33⅓% / 6-12 mo / 2-4 yrs). Real per-case data ships when we have it. */}
                    </div>
                </section>

                {/* Featured */}
                {lawyers.length > 0 && (
                    <section data-pa-section="featured" style={{ maxWidth: 1440, margin: '0 auto', padding: '48px 56px' }}>
                        <SectionHeader
                            eyebrow={`Top-rated · ${practiceArea.name}`}
                            title={`Attorneys taking new ${practiceArea.name.toLowerCase()} cases`}
                            action={<Btn kind="text" href={`/search?practice=${practiceArea.slug}`}>View all →</Btn>}
                        />
                        {typeof practiceArea.specialistsCount === 'number' && practiceArea.specialistsCount > 0 && (
                            <div style={{ fontSize: 13, color: 'var(--ink-mute)', marginBottom: 16, marginTop: -8 }}>
                                {practiceArea.specialistsCount.toLocaleString()} attorney{practiceArea.specialistsCount === 1 ? '' : 's'} list {practiceArea.name.toLowerCase()} in their bio.
                            </div>
                        )}
                        <div data-pa-featured-grid style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
                            {lawyers.slice(0, 24).map((l, i) => (
                                <div key={l.id} style={{ position: 'relative' }}>
                                    {l.specializes && (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                top: 12,
                                                right: 12,
                                                zIndex: 2,
                                                background: 'var(--ember)',
                                                color: 'var(--paper)',
                                                fontSize: 10,
                                                fontWeight: 600,
                                                letterSpacing: '.06em',
                                                textTransform: 'uppercase',
                                                padding: '4px 8px',
                                                borderRadius: 6,
                                                boxShadow: '0 1px 3px rgba(0,0,0,.15)',
                                            }}
                                            title={`Bio mentions ${practiceArea.name.toLowerCase()}`}
                                        >
                                            Specializes
                                        </div>
                                    )}
                                    <LawyerCardV data={toCard(l, i)} />
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* "When to call" section removed — copy was car-accident-specific filler that didn't generalize across practice areas. */}

                <DesignFooter />
            </div>
        </>
    );
}
